import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUnit } from "effector-react";
import "./AccountSetupPage.scss";
import { imageList } from "@/core/utils/imageList";
import { ROUTES } from "@/core/constants/app";
import { $authStore } from "@/features/auth/store/auth";
import Loader from "@/core/components/loader/Loader";
import { SEO } from "@/core/components/SEO/SEO";
import { AccountSetupPageProps } from "../model/types";
import { useAccountSetupViewModel } from "../viewmodel/useAccountSetupViewModel";
import {
  ProgressStep,
  ConfirmSkipModal,
  JobTitleStep,
  InterestsStep,
  ExpertiseStep,
  OrganizationChoiceStep,
  OrganizationDetailsStep,
  OrganizationSectorsStep,
  OrganizationDescriptionStep,
  CompletionStep,
} from "../components";
import { isFormElement } from "react-router-dom/dist/dom";

export const AccountSetupPage = ({
  initialStep = 2,
  showSkipOptions = true,
  isModal = false,
  onComplete,
  isFromSettings,
  showProgressBar = true,
  stepsToShow,
  submitPartnerOnly,
}: AccountSetupPageProps) => {
  const navigate = useNavigate();
  const authState = useUnit($authStore);

  // Skip modal state
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [pendingSkipAction, setPendingSkipAction] = useState<
    (() => void) | null
  >(null);

  // Expanded keys for tree components
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  // Search section ref for click outside
  const searchSectionRef = useRef<HTMLDivElement>(null);

  // Auth check
  useEffect(() => {
    if (!authState.loading && !authState.isAuthenticated) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [authState, navigate]);

  // ViewModel - now passes initialStep and submitPartnerOnly
  const vm = useAccountSetupViewModel(
    initialStep,
    isModal,
    onComplete,
    submitPartnerOnly,
  );

  // Initialize expanded keys
  useEffect(() => {
    setExpandedKeys(vm.initialExpandedKeys);
  }, [vm.initialExpandedKeys]);

  // Click outside handler for search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchSectionRef.current &&
        !searchSectionRef.current.contains(event.target as Node)
      ) {
        if (vm.organizationSearchResults.length > 0) {
          vm.clearSearchResults();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [vm.organizationSearchResults, vm.clearSearchResults]);

  // Skip modal handlers
  const handleSkipStep = (skipAction: () => void) => {
    setPendingSkipAction(() => skipAction);
    setShowSkipModal(true);
  };

  const confirmSkip = () => {
    if (pendingSkipAction) {
      pendingSkipAction();
      setPendingSkipAction(null);
    }
    setShowSkipModal(false);
  };

  const cancelSkip = () => {
    setPendingSkipAction(null);
    setShowSkipModal(false);
  };

  // Loading state
  if (authState.loading) {
    return <Loader message="Loading account setup..." />;
  }

  // Auth redirect
  if (!authState.isAuthenticated) {
    return null;
  }

  // Render current step
  const renderStepContent = () => {
    switch (vm.currentStep) {
      case 2:
        return <JobTitleStep onSubmit={vm.handleSubmitJobTitle} />;

      case 3:
        return (
          <InterestsStep
            sectorTreeNodes={vm.sectorTreeNodes}
            selectedNodes={vm.selectedNodes}
            expandedKeys={expandedKeys}
            isLoading={vm.sectorsLoading}
            selectedInterests={vm.selectedInterests}
            onSelectionChange={vm.setSelectedNodes}
            onExpandedChange={setExpandedKeys}
            onRemoveInterest={vm.removeInterest}
            onSubmit={vm.handleSubmitInterests}
          />
        );

      case 4:
        return (
          <ExpertiseStep
            expertiseOptions={vm.expertiseOptions}
            selectedExpertise={vm.selectedExpertise}
            isLoading={vm.expertiseLoading}
            onToggleExpertise={vm.toggleExpertise}
            onSubmit={vm.handleSubmitExpertise}
            onSkip={vm.handleSkipExpertise}
          />
        );

      case 5:
        return (
          <OrganizationChoiceStep
            searchQuery={vm.organizationSearchQuery}
            searchResults={vm.organizationSearchResults}
            searchLoading={vm.organizationSearchLoading}
            isFromSettings={isFromSettings}
            searchSectionRef={searchSectionRef}
            onSearchChange={(e) => vm.setSearchQuery(e.target.value)}
            selectedOrganizations={vm.selectedOrganizations}
            onSelectOrganization={vm.selectOrganization}
            onRemoveOrganization={vm.removeOrganization}
            proposedPartners={vm.proposedPartners}
            proposedPartnersLoading={vm.proposedPartnersLoading}
            convertToSearchResult={vm.convertToSearchResult}
            expandedSectors={vm.expandedSectors}
            onToggleSectorExpansion={vm.toggleSectorExpansion}
            onCreateOrganization={vm.handleCreateOrganization}
            onJoinSelectedOrganizations={vm.handleJoinOrganizations}
            onSkip={() => handleSkipStep(vm.handleSkipOrganization)}
            submitPartnerOnly={submitPartnerOnly}
          />
        );

      case 6:
        return (
          <OrganizationDetailsStep
            countryOptions={vm.countryOptions}
            countriesLoading={vm.countriesLoading}
            showSkipOptions={showSkipOptions}
            onSubmit={vm.handleSubmitOrganizationDetails}
            onDoItLater={() => handleSkipStep(() => vm.goToStep(9))}
          />
        );

      case 7:
        return (
          <OrganizationSectorsStep
            sectorTreeNodes={vm.sectorTreeNodes}
            selectedOrgSectors={vm.selectedOrgSectors}
            expandedKeys={expandedKeys}
            isLoading={vm.sectorsLoading}
            selectedSectorLabels={vm.selectedOrgSectorLabels}
            onSelectionChange={vm.setSelectedOrgSectors}
            onExpandedChange={setExpandedKeys}
            onRemoveSector={vm.removeOrgSector}
            onSubmit={vm.handleSubmitOrganizationSectors}
            onSkip={() => handleSkipStep(vm.handleSkipOrganizationSectors)}
            submitPartnerOnly={submitPartnerOnly}
          />
        );

      case 8:
        return (
          <OrganizationDescriptionStep
            onSubmit={vm.handleSubmitOrganizationDescription}
            onSkip={() => vm.handleSubmitOrganizationDescription("")}
            submitPartnerOnly={submitPartnerOnly}
          />
        );

      case 9:
        return (
          <CompletionStep
            formData={vm.formData}
            plans={vm.plans}
            plansLoading={vm.plansLoading}
            isCompleting={vm.isCompleting}
            completionError={vm.completionError}
            onComplete={vm.handleCompleteSetup}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="account-setup signup signup-steps">
      <SEO title="Account setup | EUcalls" description="Account setup" />

      <div className="signup-wrapper">
        <div className="signup-left">
          <div className="signup-logo">
            <img src={imageList.logo} alt="EuCalls" />
          </div>

          <div className="signup-form-wrapper">{renderStepContent()}</div>

          {showProgressBar && (
            <div className="progress-bar">
              <ProgressStep
                step={1}
                title="Set up your profile"
                isActive={vm.currentStep <= 4}
                isCompleted={vm.currentStep > 4}
              />
              {!vm.skipOrgSteps && (
                <ProgressStep
                  step={2}
                  title="Create or join your organisation"
                  isActive={[5, 6, 7, 8].includes(vm.currentStep)}
                  isCompleted={vm.currentStep > 8}
                />
              )}
              <ProgressStep
                step={vm.skipOrgSteps ? 2 : 3}
                title="Start your free trial"
                isActive={vm.currentStep === 9}
                isCompleted={false}
              />
            </div>
          )}

          <div className="signup-footer">
            &copy;{new Date().getFullYear()} EUcalls. All rights reserved.
            Powered by{" "}
            <a href="https://joistpark.eu/" target="_blank">
              JOIST
            </a>
          </div>
        </div>
      </div>

      <ConfirmSkipModal
        isOpen={showSkipModal}
        onClose={cancelSkip}
        onConfirm={confirmSkip}
      />
    </div>
  );
};
